import React, { useEffect, useState } from "react";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from "chart.js";

// Register chart components
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const Revenue = () => {
    const [revenue, setRevenue] = useState(0); // Total revenue state
    const [revenueBreakdown, setRevenueBreakdown] = useState([]); // Breakdown state
    const [transactions, setTransactions] = useState([]); // Store all transactions
    const [filteredTransactions, setFilteredTransactions] = useState([]); // For filter buttons
    const [filterStatus, setFilterStatus] = useState("all"); // all | pending | complete

    const authData = localStorage.getItem("auth");
    const authObject = JSON.parse(authData);
    const token = authObject?.accessToken;

    useEffect(() => {
        fetchRevenueData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Filter transactions based on filterStatus
        if (filterStatus === "all") {
            setFilteredTransactions(transactions);
        } else {
            setFilteredTransactions(
                transactions.filter(
                    (tx) =>
                        (filterStatus === "pending" && (tx.paymentStatus === "Pending" || tx.paymentStatus === "pending")) ||
                        (filterStatus === "complete" && (tx.paymentStatus === "Complete" || tx.paymentStatus === "complete"))
                )
            );
        }
    }, [transactions, filterStatus]);

    const fetchRevenueData = async () => {
        try {
            // Fetch revenue summary and all transactions
            const response = await axios.get(
                `${API_BASE}/finance/getRevenue`, // Should return { totalRevenue, breakdown, transactions }
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            setRevenue(response.data.data.totalRevenue);
            setRevenueBreakdown(response.data.data.breakdown || []);
            setTransactions(response.data.data.transactions || []);
        } catch (error) {
            // Dummy data for understanding if API fails
            setRevenue(32000);
            setRevenueBreakdown([
                { category: "Plumbing", amount: 8000 },
                { category: "Cleaning", amount: 4000 },
                { category: "Electrician", amount: 6000 },
                { category: "Gardening", amount: 2000 },
                { category: "Painting", amount: 3000 },
                { category: "Carpentry", amount: 2500 },
                { category: "AC Repair", amount: 3500 },
                { category: "Laundry", amount: 1000 },
                { category: "Cooking", amount: 1200 },
                { category: "Babysitting", amount: 1800 },
            ]);
            setTransactions([
                {
                    orderId: "order1",
                    userName: "Ali Ahmed",
                    providerName: "Provider A",
                    totalCharges: 2000,
                    transactionFee: 600,
                    date: new Date().toISOString(),
                    paymentStatus: "Pending",
                },
                {
                    orderId: "order2",
                    userName: "Sara Khan",
                    providerName: "Provider B",
                    totalCharges: 4000,
                    transactionFee: 1200,
                    date: new Date().toISOString(),
                    paymentStatus: "Complete",
                },
                {
                    orderId: "order3",
                    userName: "Bilal Hussain",
                    providerName: "Provider C",
                    totalCharges: 3500,
                    transactionFee: 1050,
                    date: new Date().toISOString(),
                    paymentStatus: "Pending",
                },
                {
                    orderId: "order4",
                    userName: "Fatima Noor",
                    providerName: "Provider D",
                    totalCharges: 2000,
                    transactionFee: 600,
                    date: new Date().toISOString(),
                    paymentStatus: "Complete",
                },
                {
                    orderId: "order5",
                    userName: "Usman Tariq",
                    providerName: "Provider E",
                    totalCharges: 3000,
                    transactionFee: 900,
                    date: new Date().toISOString(),
                    paymentStatus: "Pending",
                },
                {
                    orderId: "order6",
                    userName: "Ayesha Siddiqui",
                    providerName: "Provider F",
                    totalCharges: 5000,
                    transactionFee: 1500,
                    date: new Date().toISOString(),
                    paymentStatus: "Complete",
                },
                {
                    orderId: "order7",
                    userName: "Imran Malik",
                    providerName: "Provider G",
                    totalCharges: 2500,
                    transactionFee: 750,
                    date: new Date().toISOString(),
                    paymentStatus: "Pending",
                },
                {
                    orderId: "order8",
                    userName: "Zainab Raza",
                    providerName: "Provider H",
                    totalCharges: 1000,
                    transactionFee: 300,
                    date: new Date().toISOString(),
                    paymentStatus: "Complete",
                },
                {
                    orderId: "order9",
                    userName: "Hamza Shah",
                    providerName: "Provider I",
                    totalCharges: 1800,
                    transactionFee: 540,
                    date: new Date().toISOString(),
                    paymentStatus: "Pending",
                },
                {
                    orderId: "order10",
                    userName: "Mehwish Ali",
                    providerName: "Provider J",
                    totalCharges: 1800,
                    transactionFee: 540,
                    date: new Date().toISOString(),
                    paymentStatus: "Complete",
                },
            ]);
            console.error("Error fetching revenue data, using dummy data:", error);
        }
    };

    // Calculate the amount after cutting 30% for each category
    const amountAfterCutting30 = revenueBreakdown.length
        ? revenueBreakdown.map((item) => ({
            category: item.category,
            amountAfterCut: item.amount * 0.7, // Apply 30% cut
        }))
        : [];

    // Calculate total amount after cutting 30%
    const totalAmountAfterCut = amountAfterCutting30.reduce(
        (total, item) => total + item.amountAfterCut,
        0
    );

    const chartData = {
        labels: revenueBreakdown.map((item) => item.category),
        datasets: [
            {
                label: "Revenue",
                data: revenueBreakdown.map((item) => item.amount),
                backgroundColor: "#4CAF50",
                borderWidth: 1,
            },
            {
                label: "Amount After Cutting 30%",
                data: amountAfterCutting30.map((item) => item.amountAfterCut),
                backgroundColor: "#FF5722",
                borderWidth: 1,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                display: true,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                },
            },
        },
    };
const API_BASE = process.env.REACT_APP_API_BASE;

    return (
        <div className="p-6">
            <h3 className="text-2xl font-bold mb-4">Revenue Overview</h3>

            {/* Filter Buttons */}
            <div className="flex gap-4 mb-4">
                <button
                    className={`px-4 py-2 rounded-md border ${filterStatus === "all" ? "bg-gray-300" : "bg-white"} hover:bg-gray-200`}
                    onClick={() => setFilterStatus("all")}
                >
                    All
                </button>
                <button
                    className={`px-4 py-2 rounded-md border ${filterStatus === "pending" ? "bg-yellow-200" : "bg-white"} hover:bg-yellow-100`}
                    onClick={() => setFilterStatus("pending")}
                >
                    Pending
                </button>
                <button
                    className={`px-4 py-2 rounded-md border ${filterStatus === "complete" ? "bg-green-200" : "bg-white"} hover:bg-green-100`}
                    onClick={() => setFilterStatus("complete")}
                >
                    Complete
                </button>
            </div>

            {/* Total Revenue Card */}
            <div className="bg-green-100 p-6 rounded-lg shadow-md mb-6">
                <h4 className="text-xl font-semibold">Total Revenue</h4>
                <p className="text-3xl font-semibold text-green-700 mt-2">
                    PKR: {revenue.toLocaleString()}
                </p>
            </div>

            {/* Amount After Cutting 30% Card */}
            <div className="bg-blue-100 p-6 rounded-lg shadow-md mb-6">
                <h4 className="text-xl font-semibold">Amount After Cutting 30%</h4>
                <p className="text-3xl font-semibold text-blue-700 mt-2">
                    PKR: {totalAmountAfterCut.toLocaleString() || 0}
                </p>
            </div>

            {/* Transactions Table */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h4 className="text-xl font-semibold mb-4">Order Transactions</h4>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            <th className="p-2 text-left">Order ID</th>
                            <th className="p-2 text-left">User</th>
                            <th className="p-2 text-left">Provider</th>
                            <th className="p-2 text-left">Total Charges</th>
                            <th className="p-2 text-left">Transaction Fee</th>
                            <th className="p-2 text-left">Remain Amount</th>
                            <th className="p-2 text-left">Payment Status</th>
                            <th className="p-2 text-left">Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTransactions.length > 0 ? (
                            filteredTransactions.map((tx, idx) => (
                                <tr key={idx} className="border-b">
                                    <td className="p-2">{tx.orderId}</td>
                                    <td className="p-2">{tx.userName || "-"}</td>
                                    <td className="p-2">{tx.providerName || "-"}</td>
                                    <td className="p-2">PKR {tx.totalCharges}</td>
                                    <td className="p-2">PKR {tx.transactionFee}</td>
                                    <td className="p-2">PKR {tx.totalCharges - tx.transactionFee}</td>
                                    <td className="p-2">
                                        <span className={`px-2 py-1 rounded text-xs font-semibold ${tx.paymentStatus === "Complete" || tx.paymentStatus === "complete" ? "bg-green-200 text-green-800" : "bg-yellow-200 text-yellow-800"}`}>
                                            {tx.paymentStatus || "Pending"}
                                        </span>
                                    </td>
                                    <td className="p-2">{new Date(tx.date).toLocaleString()}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={8} className="p-2 text-center">No transactions found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Revenue Chart */}
            <div className="mt-8">
                <Bar data={chartData} options={chartOptions} />
            </div>
        </div>
    );
};

export default Revenue;
//                             Subtract from Revenue
//                         </button>
//                     </div>
//                     {/* Error Message */}
//                     {error && <p className="text-red-500 text-sm">{error}</p>}
//                 </div>
//             </div>

//             {/* Recent Adjustments Log */}
//             <div className="bg-white p-6 rounded-lg shadow-md">
//                 <h4 className="text-xl font-semibold mb-4">Recent Adjustments</h4>
//                 {adjustments.length > 0 ? (
//                     <ul className="space-y-2">
//                         {adjustments.map((adjustment, index) => (
//                             <li key={index} className="text-sm">
//                                 {adjustment.type === "add" ? "+" : "-"}PKR {adjustment.amount.toLocaleString()} (
//                                 {adjustment.timestamp})
//                             </li>
//                         ))}
//                     </ul>
//                 ) : (
//                     <p>No adjustments made yet.</p>
//                 )}
//             </div>

//             {/* Revenue Chart */}
//             <div className="mt-8">
//                 <Bar data={chartData} options={chartOptions} />
//             </div>
//         </div>
//     );
// };

// export default Revenue;

// import React, { useState } from "react";
// import jsPDF from "jspdf";
// import "jspdf-autotable";
// import {
//   Chart as ChartJS,
//   LineElement,
//   PointElement,
//   CategoryScale,
//   LinearScale,
//   Tooltip,
//   Legend,
//   Title,
// } from "chart.js";

// // Register Line chart components
// ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Title);

// const dummyUserRevenue = [
//   { name: "Ali", amount: 5000, date: "2025-06-10" },
//   { name: "Tahir", amount: 7000, date: "2025-06-15" },
//   { name: "Ayan", amount: 3000, date: "2025-06-22" },
// ];

// const dummyProviderRevenue = [
//   { name: "Zahid", amount: 8000, date: "2025-06-11" },
//   { name: "Khan", amount: 6500, date: "2025-06-18" },
//   { name: "Raza", amount: 4000, date: "2025-06-23" },
// ];

// const Revenue = () => {
//   const [reportDays, setReportDays] = useState(15);

//   const userData = dummyUserRevenue;
//   const providerData = dummyProviderRevenue;


//   const exportPDF = () => {
//     const doc = new jsPDF();
//     doc.text(`${reportDays}-Day Revenue Report`, 14, 15);

//     doc.autoTable({
//       startY: 25,
//       head: [["User Name", "Amount", "Date"]],
//       body: userData.map((item) => [item.name, `PKR ${item.amount}`, item.date]),
//     });

//     doc.autoTable({
//       startY: doc.lastAutoTable.finalY + 10,
//       head: [["Provider Name", "Amount", "Date"]],
//       body: providerData.map((item) => [item.name, `PKR ${item.amount}`, item.date]),
//     });

//     doc.save("Revenue_Report.pdf");
//   };

//   return (
//     <div className="p-6 bg-gray-100 min-h-screen">
//       <div className="max-w-6xl mx-auto">
//         <div className="flex justify-between items-center mb-6">
//           <h2 className="text-2xl font-bold text-gray-800">Revenue Report</h2>
//           <div className="flex gap-4">
//             <select
//               value={reportDays}
//               onChange={(e) => setReportDays(Number(e.target.value))}
//               className="px-4 py-2 rounded-md border border-gray-300 bg-white"
//             >
//               <option value={15}>15-Day Report</option>
//               <option value={30}>30-Day Report</option>
//             </select>
//             <button
//               onClick={exportPDF}
//               className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
//             >
//               Export PDF
//             </button>
//           </div>
//         </div>

//         {/* Tables Section */}
//         <div className="grid md:grid-cols-2 gap-8">
//           {/* User Table */}
//           <div className="bg-white p-4 rounded-lg shadow">
//             <h3 className="text-lg font-semibold text-blue-600 mb-3">User Revenue</h3>
//             <table className="w-full text-sm">
//               <thead>
//                 <tr className="bg-blue-100">
//                   <th className="p-2 text-left">Name</th>
//                   <th className="p-2 text-left">Amount</th>
//                   <th className="p-2 text-left">Date</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {userData.map((item, idx) => (
//                   <tr key={idx} className="border-b">
//                     <td className="p-2">{item.name}</td>
//                     <td className="p-2">PKR {item.amount}</td>
//                     <td className="p-2">{item.date}</td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>

//           {/* Provider Table */}
//           <div className="bg-white p-4 rounded-lg shadow">
//             <h3 className="text-lg font-semibold text-orange-600 mb-3">Provider Revenue</h3>
//             <table className="w-full text-sm">
//               <thead>
//                 <tr className="bg-orange-100">
//                   <th className="p-2 text-left">Name</th>
//                   <th className="p-2 text-left">Amount</th>
//                   <th className="p-2 text-left">Date</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {providerData.map((item, idx) => (
//                   <tr key={idx} className="border-b">
//                     <td className="p-2">{item.name}</td>
//                     <td className="p-2">PKR {item.amount}</td>
//                     <td className="p-2">{item.date}</td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         </div>

//       </div>
//     </div>
//   );
// };

// export default Revenue;
